import React, { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getUsers } from '../../services/userService';
import { useDebounce } from '../../hooks/useDebounce';

export interface PickedUser {
  id: number;
  firebaseUid: string;
  name?: string | null;
  phone?: string | null;
  role?: string | null;
}

interface Props {
  label?: string;
  /** Restrict search to a role (e.g. 'driver' for charge waivers). */
  roleFilter?: 'rider' | 'driver';
  value: PickedUser | null;
  onChange: (user: PickedUser | null) => void;
  helperText?: string;
}

/**
 * Type a phone number (or name) and pick the matching user — no raw IDs.
 * Server-side searched + debounced; returns the full user (with firebaseUid).
 */
const PhoneUserAutocomplete: React.FC<Props> = ({
  label = 'Search by phone or name',
  roleFilter,
  value,
  onChange,
  helperText,
}) => {
  const [input, setInput] = useState('');
  const debounced = useDebounce(input, 350);
  const ready = debounced.trim().length >= 3;

  const { data, isFetching } = useQuery({
    queryKey: ['user-search', debounced, roleFilter],
    queryFn: async () => {
      const res = await getUsers({ search: debounced.trim(), limit: 10, role: roleFilter });
      const list = res.data?.data ?? res.data?.items ?? (Array.isArray(res.data) ? res.data : []);
      return list as PickedUser[];
    },
    enabled: ready,
  });

  const options = data ?? [];

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInput(v)}
      options={options}
      getOptionLabel={(o) => (o ? `${o.phone ?? ''}${o.name ? ' · ' + o.name : ''}`.trim() : '')}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      loading={isFetching}
      filterOptions={(x) => x} // server already filtered
      noOptionsText={ready ? 'No matching user' : 'Type at least 3 characters'}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ fontWeight: 700 }}>{o.phone}</Box>
            {o.name && <Box sx={{ color: 'text.secondary' }}>· {o.name}</Box>}
            {o.role && <Chip size="small" label={o.role} sx={{ ml: 'auto' }} />}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default PhoneUserAutocomplete;
